import {Modal, Button, Spinner, Alert} from 'react-bootstrap'
import Select from 'react-select'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useMutation} from '@apollo/client'
import {UPDATE_POLICIES_STATUS} from '../../../gql/mutations/policyMutation'
import {PolicyFilters} from '../policiesTypes'
import {PAYMENT_STATUS_OPTIONS, DOCUMENT_STATUS_OPTIONS} from '../../../helpers/consts'
import {PolicyStatus} from '../../../types/policies'
import {AppliedFiltersList, FilterConfig, DateRangeFilter} from './AppliedFiltersList'
import {parseId} from '../../../helpers'
import {
  buildPolicyReassignFilterVariables,
  getActiveFilterSummary,
} from './ModalReassignPolicies'

const M = defineMessages({
  title: {id: 'policies.list.update.title', defaultMessage: 'Update policies'},
  sectionAffected: {id: 'policies.list.update.section.affected', defaultMessage: 'Affected policies'},
  sectionNewValues: {id: 'policies.list.update.section.newValues', defaultMessage: 'New values'},
  count: {id: 'policies.list.update.count', defaultMessage: '{count, plural, one {# policy} other {# policies}}'},
  selectedCount: {id: 'policies.list.update.selectedCount', defaultMessage: '{count, plural, one {# policy selected:} other {# policies selected:}}'},
  badgeImportant: {id: 'policies.list.update.badge.important', defaultMessage: 'Important'},
  badgeArchived: {id: 'policies.list.update.badge.archived', defaultMessage: 'Archived'},
  badgeHasReminders: {id: 'policies.list.update.badge.hasReminders', defaultMessage: 'Has reminders'},
  badgeYears: {id: 'policies.list.update.badge.years', defaultMessage: 'Years: {years}'},
  badgeMember: {id: 'policies.list.update.badge.member', defaultMessage: 'Member: {value}'},
  badgePolicy: {id: 'policies.list.update.badge.policy', defaultMessage: 'Policy: {value}'},
  emptyNoSource: {id: 'policies.list.update.emptyNoSource', defaultMessage: 'No policies selected and no filters applied. Select policies or apply filters first.'},
  fieldPolicyStatus: {id: 'policies.list.update.field.policyStatus', defaultMessage: 'Policy status'},
  fieldDocumentStatus: {id: 'policies.list.update.field.documentStatus', defaultMessage: 'Document status'},
  fieldPaymentStatus: {id: 'policies.list.update.field.paymentStatus', defaultMessage: 'Payment status'},
  fieldPolicyType: {id: 'policies.list.update.field.policyType', defaultMessage: 'Policy type'},
  fieldCarrier: {id: 'policies.list.update.field.carrier', defaultMessage: 'Carrier'},
  phKeepUnchanged: {id: 'policies.list.update.placeholder.keepUnchanged', defaultMessage: 'Keep unchanged'},
  confirmAreYouSure: {id: 'policies.list.update.confirm.areYouSure', defaultMessage: 'Are you sure?'},
  confirmFinal: {id: 'policies.list.update.confirm.final', defaultMessage: 'Final confirmation'},
  changesToApply: {id: 'policies.list.update.changesToApply', defaultMessage: 'Changes to apply:'},
  changePolicyStatus: {id: 'policies.list.update.change.policyStatus', defaultMessage: 'Policy status → {value}'},
  changeDocumentStatus: {id: 'policies.list.update.change.documentStatus', defaultMessage: 'Document status → {value}'},
  changePaymentStatus: {id: 'policies.list.update.change.paymentStatus', defaultMessage: 'Payment status → {value}'},
  changePolicyType: {id: 'policies.list.update.change.policyType', defaultMessage: 'Policy type → {value}'},
  changeCarrier: {id: 'policies.list.update.change.carrier', defaultMessage: 'Carrier → {value}'},
  labelSelectedPolicies: {id: 'policies.list.update.label.selectedPolicies', defaultMessage: 'Selected policies:'},
  labelActiveFilters: {id: 'policies.list.update.label.activeFilters', defaultMessage: 'Active filters:'},
  btnGoBack: {id: 'policies.list.update.btn.goBack', defaultMessage: 'Go back'},
  btnYesContinue: {id: 'policies.list.update.btn.yesContinue', defaultMessage: 'Yes, continue'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

// Shared status-option catalogs (frontend consts) reuse the filters.* translations.
const PAYMENT_STATUS_MSG_IDS: Record<string, string> = {
  PAID: 'policies.list.filters.ps.paid',
  FAILED: 'policies.list.filters.ps.failed',
  PENDING: 'policies.list.filters.ps.pending',
  AUTO_PAY: 'policies.list.filters.ps.autoPay',
  NOT_APPLICABLE: 'policies.list.filters.ps.notApplicable',
  PAGA_POR_SU_CUENTA: 'policies.list.filters.ps.pagaPorSuCuenta',
}
const DOCUMENT_STATUS_MSG_IDS: Record<string, string> = {
  ACTIVE: 'policies.list.filters.ds.active',
  PENDING: 'policies.list.filters.ds.pending',
  PROCESSING: 'policies.list.filters.ds.processing',
  DECLINED: 'policies.list.filters.ds.declined',
  COMPLETED: 'policies.list.filters.ds.completed',
}

interface SelectedPolicy {
  id: string
  name: string
  status: string
  user: string
  agent: string
}

interface ModalUpdatePoliciesProps {
  isOpen: boolean
  onClose: () => void
  updateListPolicies: () => void
  filters: PolicyFilters
  tabFilters: string
  selectedYears: Array<string | number>
  searchByMembers: boolean
  totalItems: number
  policyStatuses: PolicyStatus[]
  policyTypeOptions: PolicyTypeOption[]
  carrierOptions: CarrierOption[]
  filtersConfig: FilterConfig[]
  dateRangeFilters: DateRangeFilter[]
  formatDate: (date: string) => string
  selectedPolicies: SelectedPolicy[]
}

interface IResUpdatePoliciesStatus {
  updatePoliciesStatus: {
    success: boolean
    message: string
    errors?: {field: string; message: string}[]
  }
}

interface StatusOption {
  value: string
  label: string
}

interface PolicyTypeOption {
  value: number
  label: string
}

interface CarrierOption {
  value: number
  label: string
}

type Step = 'form' | 'confirm1' | 'confirm2'

export const ModalUpdatePolicies = ({
  isOpen,
  onClose,
  updateListPolicies,
  filters,
  tabFilters,
  selectedYears,
  searchByMembers,
  totalItems,
  policyStatuses,
  policyTypeOptions,
  carrierOptions,
  filtersConfig,
  dateRangeFilters,
  formatDate,
  selectedPolicies,
}: ModalUpdatePoliciesProps) => {
  const intl = useIntl()
  const [step, setStep] = useState<Step>('form')
  const [newStatus, setNewStatus] = useState<StatusOption | null>(null)
  const [newPaymentStatus, setNewPaymentStatus] = useState<StatusOption | null>(null)
  const [newDocumentStatus, setNewDocumentStatus] = useState<StatusOption | null>(null)
  const [newPolicyType, setNewPolicyType] = useState<PolicyTypeOption | null>(null)
  const [newCarrier, setNewCarrier] = useState<CarrierOption | null>(null)

  const [gqlCall, {data, loading, error}] = useMutation<IResUpdatePoliciesStatus>(
    UPDATE_POLICIES_STATUS
  )

  const bySelected = selectedPolicies.length > 0

  const activeFilterSummary = useMemo(
    () => getActiveFilterSummary(filters, tabFilters, selectedYears, searchByMembers),
    [filters, searchByMembers, selectedYears, tabFilters]
  )
  const hasFilterSource = activeFilterSummary.length > 0

  const hasSource = bySelected || hasFilterSource
  const disabledUpdate =
    !hasSource ||
    (!newStatus && !newPaymentStatus && !newDocumentStatus && !newPolicyType && !newCarrier)

  const affectedCount = bySelected ? selectedPolicies.length : totalItems
  const countLabel = intl.formatMessage(M.count, {count: affectedCount})

  const policyStatusOptions = useMemo<StatusOption[]>(() => {
    return policyStatuses.map((s) => ({value: s.code, label: s.title}))
  }, [policyStatuses])

  // Document/Payment status options come from frontend consts → translate labels
  // (reusing the filters.* catalog). Policy status comes from the DB → left as-is.
  const documentStatusOptions = (DOCUMENT_STATUS_OPTIONS as StatusOption[]).map((o) => ({
    value: o.value,
    label: DOCUMENT_STATUS_MSG_IDS[o.value]
      ? intl.formatMessage({id: DOCUMENT_STATUS_MSG_IDS[o.value], defaultMessage: o.label})
      : o.label,
  }))
  const paymentStatusOptions = (PAYMENT_STATUS_OPTIONS as StatusOption[]).map((o) => ({
    value: o.value,
    label: PAYMENT_STATUS_MSG_IDS[o.value]
      ? intl.formatMessage({id: PAYMENT_STATUS_MSG_IDS[o.value], defaultMessage: o.label})
      : o.label,
  }))

  const modalClose = useCallback(() => {
    setStep('form')
    setNewStatus(null)
    setNewPaymentStatus(null)
    setNewDocumentStatus(null)
    setNewPolicyType(null)
    setNewCarrier(null)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) setStep('form')
  }, [isOpen])

  useEffect(() => {
    if (error)
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
  }, [error, intl])

  useEffect(() => {
    if (!data) return
    if (data.updatePoliciesStatus?.success) {
      toast.success(data.updatePoliciesStatus.message)
      updateListPolicies()
      modalClose()
      return
    }
    const message = data.updatePoliciesStatus?.errors?.[0]?.message
    if (message) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message}))
  }, [data, modalClose, updateListPolicies, intl])

  const handleConfirm1 = () => {
    if (disabledUpdate) return
    setStep('confirm1')
  }

  const handleConfirm2 = () => setStep('confirm2')

  const handleExecute = () => {
    const base: Record<string, unknown> = {
      newStatus: newStatus?.value ?? null,
      newPaymentStatus: newPaymentStatus?.value ?? null,
      newDocumentStatus: newDocumentStatus?.value ?? null,
      newPolicyType: newPolicyType?.value ?? null,
      newCarrier: newCarrier?.value ?? null,
    }

    if (bySelected) {
      base.policyIds = selectedPolicies
        .map((p) => Number(parseId(p.id)))
        .filter((id) => Number.isFinite(id) && id > 0)
    } else {
      Object.assign(
        base,
        buildPolicyReassignFilterVariables(filters, tabFilters, selectedYears, searchByMembers)
      )
    }

    gqlCall({variables: base})
  }

  const selectedChanges: string[] = []
  if (newStatus) selectedChanges.push(intl.formatMessage(M.changePolicyStatus, {value: newStatus.label}))
  if (newDocumentStatus) selectedChanges.push(intl.formatMessage(M.changeDocumentStatus, {value: newDocumentStatus.label}))
  if (newPaymentStatus) selectedChanges.push(intl.formatMessage(M.changePaymentStatus, {value: newPaymentStatus.label}))
  if (newPolicyType) selectedChanges.push(intl.formatMessage(M.changePolicyType, {value: newPolicyType.label}))
  if (newCarrier) selectedChanges.push(intl.formatMessage(M.changeCarrier, {value: newCarrier.label}))

  return (
    <Modal show={isOpen} onHide={modalClose} size='lg' className='modal-update-policies'>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className='fw-bold'>{intl.formatMessage(M.title)}</span>
        </Modal.Title>
      </Modal.Header>

      {step === 'form' && (
        <>
          <Modal.Body>
            <div className='d-flex flex-column gap-4'>
              <div>
                <h2 className='fs-3 mb-3'>{intl.formatMessage(M.sectionAffected)}</h2>
                <div className='rounded border p-3 bg-light'>
                  {bySelected ? (
                    <div className='d-flex flex-column gap-1'>
                      <div className='fw-semibold mb-1'>
                        {intl.formatMessage(M.selectedCount, {count: selectedPolicies.length})}
                      </div>
                      <div className='d-flex flex-wrap gap-2'>
                        {selectedPolicies.map((p) => (
                          <span key={p.id} className='badge bg-primary-subtle text-dark'>
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : hasFilterSource ? (
                    <div className='d-flex flex-column gap-2'>
                      {(tabFilters === 'Important' || tabFilters === 'Archived' || tabFilters === 'Reminders' || selectedYears.length > 0 || filters.policyName) && (
                        <div className='d-flex flex-wrap gap-2'>
                          {tabFilters === 'Important' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeImportant)}</span>}
                          {tabFilters === 'Archived' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeArchived)}</span>}
                          {tabFilters === 'Reminders' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeHasReminders)}</span>}
                          {selectedYears.length > 0 && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeYears, {years: selectedYears.join(', ')})}</span>}
                          {filters.policyName && <span className='badge bg-secondary-subtle text-dark'>{searchByMembers ? intl.formatMessage(M.badgeMember, {value: filters.policyName}) : intl.formatMessage(M.badgePolicy, {value: filters.policyName})}</span>}
                        </div>
                      )}
                      <AppliedFiltersList
                        filters={filters}
                        config={filtersConfig}
                        dateRangeFilters={dateRangeFilters}
                        formatDate={formatDate}
                      />
                    </div>
                  ) : (
                    <span className='text-muted'>
                      {intl.formatMessage(M.emptyNoSource)}
                    </span>
                  )}
                </div>
                {hasSource && (
                  <div className='mt-2 text-muted fs-7'>
                    <FormattedMessage
                      id='policies.list.update.willBeAffected'
                      defaultMessage='<b>{count}</b> will be affected.'
                      values={{count: countLabel, b: (chunks) => <strong>{chunks}</strong>}}
                    />
                  </div>
                )}
              </div>

              <div>
                <h2 className='fs-3 mb-3'>{intl.formatMessage(M.sectionNewValues)}</h2>
                <div className='d-flex flex-column gap-3'>
                  <div>
                    <div className='mb-2 fw-semibold'>{intl.formatMessage(M.fieldPolicyStatus)}</div>
                    <Select<StatusOption, false>
                      value={newStatus}
                      options={policyStatusOptions}
                      onChange={(selected) => setNewStatus(selected)}
                      placeholder={intl.formatMessage(M.phKeepUnchanged)}
                      isClearable
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>{intl.formatMessage(M.fieldDocumentStatus)}</div>
                    <Select<StatusOption, false>
                      value={newDocumentStatus}
                      options={documentStatusOptions}
                      onChange={(selected) => setNewDocumentStatus(selected)}
                      placeholder={intl.formatMessage(M.phKeepUnchanged)}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>{intl.formatMessage(M.fieldPaymentStatus)}</div>
                    <Select<StatusOption, false>
                      value={newPaymentStatus}
                      options={paymentStatusOptions}
                      onChange={(selected) => setNewPaymentStatus(selected)}
                      placeholder={intl.formatMessage(M.phKeepUnchanged)}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>{intl.formatMessage(M.fieldPolicyType)}</div>
                    <Select<PolicyTypeOption, false>
                      value={newPolicyType}
                      options={policyTypeOptions}
                      onChange={(selected) => setNewPolicyType(selected)}
                      placeholder={intl.formatMessage(M.phKeepUnchanged)}
                      isClearable
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>{intl.formatMessage(M.fieldCarrier)}</div>
                    <Select<CarrierOption, false>
                      value={newCarrier}
                      options={carrierOptions}
                      onChange={(selected) => setNewCarrier(selected)}
                      placeholder={intl.formatMessage(M.phKeepUnchanged)}
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={modalClose} variant='light'>
              {intl.formatMessage(M.cancel)}
            </Button>
            <Button onClick={handleConfirm1} variant='primary' disabled={disabledUpdate}>
              <i className='bi bi-pencil-square' /> {intl.formatMessage(M.title)}
            </Button>
          </Modal.Footer>
        </>
      )}

      {(step === 'confirm1' || step === 'confirm2') && (
        <>
          <Modal.Body>
            <Alert variant={step === 'confirm1' ? 'warning' : 'danger'} className='mb-3'>
              <Alert.Heading>
                <i className={`bi ${step === 'confirm1' ? 'bi-exclamation-triangle-fill' : 'bi-exclamation-octagon-fill'} me-2`} />
                {step === 'confirm1' ? intl.formatMessage(M.confirmAreYouSure) : intl.formatMessage(M.confirmFinal)}
              </Alert.Heading>
              <p className='mb-0'>
                {step === 'confirm1' ? (
                  <FormattedMessage
                    id='policies.list.update.confirm1Body'
                    defaultMessage='You are about to update <b>{count}</b>. This action <b>cannot be undone</b>.'
                    values={{count: countLabel, b: (chunks) => <strong>{chunks}</strong>}}
                  />
                ) : (
                  <FormattedMessage
                    id='policies.list.update.confirm2Body'
                    defaultMessage='Are you <b>absolutely sure</b>? You are about to permanently update <b>{count}</b>. There is no way to reverse this automatically.'
                    values={{count: countLabel, b: (chunks) => <strong>{chunks}</strong>}}
                  />
                )}
              </p>
            </Alert>

            <div className='mb-3'>
              <div className='fw-semibold mb-2'>{intl.formatMessage(M.changesToApply)}</div>
              <ul className='mb-0'>
                {selectedChanges.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>

            <div className='mb-0'>
              <div className='fw-semibold mb-2'>{bySelected ? intl.formatMessage(M.labelSelectedPolicies) : intl.formatMessage(M.labelActiveFilters)}</div>
              <div className='rounded border p-2 bg-light'>
                {bySelected ? (
                  <div className='d-flex flex-wrap gap-2'>
                    {selectedPolicies.map((p) => (
                      <span key={p.id} className='badge bg-primary-subtle text-dark'>
                        {p.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {(tabFilters === 'Important' || tabFilters === 'Archived' || tabFilters === 'Reminders' || selectedYears.length > 0 || filters.policyName) && (
                      <div className='d-flex flex-wrap gap-2'>
                        {tabFilters === 'Important' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeImportant)}</span>}
                        {tabFilters === 'Archived' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeArchived)}</span>}
                        {tabFilters === 'Reminders' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeHasReminders)}</span>}
                        {selectedYears.length > 0 && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeYears, {years: selectedYears.join(', ')})}</span>}
                        {filters.policyName && <span className='badge bg-secondary-subtle text-dark'>{searchByMembers ? intl.formatMessage(M.badgeMember, {value: filters.policyName}) : intl.formatMessage(M.badgePolicy, {value: filters.policyName})}</span>}
                      </div>
                    )}
                    <AppliedFiltersList
                      filters={filters}
                      config={filtersConfig}
                      dateRangeFilters={dateRangeFilters}
                      formatDate={formatDate}
                    />
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setStep(step === 'confirm1' ? 'form' : 'confirm1')} variant='light'>
              {step === 'confirm1' ? intl.formatMessage(M.cancel) : intl.formatMessage(M.btnGoBack)}
            </Button>
            {step === 'confirm1' ? (
              <Button onClick={handleConfirm2} variant='warning'>
                {intl.formatMessage(M.btnYesContinue)}
              </Button>
            ) : (
              <Button onClick={handleExecute} variant='danger' disabled={loading}>
                {loading && <Spinner animation='border' className='me-2' size='sm' />}
                <i className='bi bi-check-circle' /> {intl.formatMessage({id: 'policies.list.update.btn.yesUpdate', defaultMessage: 'Yes, update {count}'}, {count: countLabel})}
              </Button>
            )}
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}
